import React, { useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ArrowRight, Lock, BookOpen, Shield, Clock, X, ThumbsUp, MessageCircle, Share2, MoreHorizontal, ShieldCheck, Menu, FileText, Headphones, Download, Zap, Play, Pause, Volume2, Gift, MessageSquare, Heart } from 'lucide-react';
// --- Animated Background Waves ---
const AnimatedWaves = ({
  className = ''
}: {
  className?: string;
}) => {
  return <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`} data-id="element-2">
      {/* Wave 1 - Slow, large amplitude */}
      <motion.svg className="absolute w-[200%] h-32 md:h-48 top-1/4 -left-1/4" viewBox="0 0 1440 120" preserveAspectRatio="none" initial={{
      x: 0
    }} animate={{
      x: [0, -720, 0]
    }} transition={{
      duration: 20,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-3">
        <motion.path d="M0,60 C180,120 360,0 540,60 C720,120 900,0 1080,60 C1260,120 1440,0 1620,60 C1800,120 1980,0 2160,60" fill="none" stroke="rgba(201, 169, 98, 0.08)" strokeWidth="2" initial={{
        pathLength: 0,
        opacity: 0
      }} animate={{
        pathLength: 1,
        opacity: 1
      }} transition={{
        duration: 2,
        ease: 'easeOut'
      }} data-id="element-4" />
      </motion.svg>

      {/* Wave 2 - Medium speed, offset */}
      <motion.svg className="absolute w-[200%] h-24 md:h-36 top-1/2 -left-1/4" viewBox="0 0 1440 100" preserveAspectRatio="none" initial={{
      x: -200
    }} animate={{
      x: [-200, -920, -200]
    }} transition={{
      duration: 25,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-5">
        <motion.path d="M0,50 C120,90 240,10 360,50 C480,90 600,10 720,50 C840,90 960,10 1080,50 C1200,90 1320,10 1440,50 C1560,90 1680,10 1800,50 C1920,90 2040,10 2160,50" fill="none" stroke="rgba(201, 169, 98, 0.05)" strokeWidth="1.5" data-id="element-6" />
      </motion.svg>

      {/* Wave 3 - Faster, smaller amplitude (like a heartbeat/pulse) */}
      <motion.svg className="absolute w-[200%] h-20 md:h-28 top-3/4 -left-1/4" viewBox="0 0 1440 80" preserveAspectRatio="none" initial={{
      x: -100
    }} animate={{
      x: [-100, -820, -100]
    }} transition={{
      duration: 15,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-7">
        <motion.path d="M0,40 C90,60 180,20 270,40 C360,60 450,20 540,40 C630,60 720,20 810,40 C900,60 990,20 1080,40 C1170,60 1260,20 1350,40 C1440,60 1530,20 1620,40 C1710,60 1800,20 1890,40 C1980,60 2070,20 2160,40" fill="none" stroke="rgba(201, 169, 98, 0.06)" strokeWidth="1" data-id="element-8" />
      </motion.svg>
    </div>;
};
// --- Pulse Glow Effect ---
const PulseGlow = ({
  className = ''
}: {
  className?: string;
}) => {
  return <motion.div className={`absolute rounded-full bg-[#c9a962]/10 blur-3xl ${className}`} animate={{
    scale: [1, 1.2, 1],
    opacity: [0.1, 0.2, 0.1]
  }} transition={{
    duration: 8,
    repeat: Infinity,
    ease: 'easeInOut'
  }} data-id="element-9" />;
};
// --- Components ---
const Section = ({
  children,
  className = '',
  darker = false,
  id = ''
}: {
  children: React.ReactNode;
  className?: string;
  darker?: boolean;
  id?: string;
}) => <section id={id} className={`py-10 md:py-20 px-4 md:px-6 ${darker ? 'bg-[#0a0d12]' : ''} ${className}`} data-id="element-10">
    <div className="max-w-3xl mx-auto" data-id="element-11">{children}</div>
  </section>;
const Button = ({
  children,
  secondary = false,
  className = '',
  onClick,
  href
}: {
  children: React.ReactNode;
  secondary?: boolean;
  className?: string;
  onClick?: () => void;
  href?: string;
}) => {
  const handleClick = () => {
    if (href) {
      window.location.href = href;
    } else if (onClick) {
      onClick();
    }
  };
  return <button onClick={handleClick} className={`
        w-full md:w-auto px-6 md:px-8 py-3 md:py-4 rounded-lg text-base md:text-lg font-semibold transition-all duration-200 transform hover:-translate-y-0.5
        ${secondary ? 'bg-transparent border-2 border-[#c9a962] text-[#c9a962] hover:bg-[#c9a962]/10' : 'bg-[#c9a962] text-[#0f1419] hover:bg-[#d4b46e] shadow-lg shadow-[#c9a962]/20'}
        ${className}
      `} data-id="element-12">
      {children}
    </button>;
};
const CheckItem = ({
  children
}: {
  children: React.ReactNode;
}) => <div className="flex items-start space-x-3 mb-3" data-id="element-13">
    <div className="mt-0.5 flex-shrink-0 w-5 h-5 md:w-6 md:h-6 rounded-full bg-[#c9a962]/20 flex items-center justify-center" data-id="element-14">
      <Check className="w-3 h-3 md:w-4 md:h-4 text-[#c9a962]" data-id="element-15" />
    </div>
    <p className="text-base md:text-lg text-[#f5f0e8]/90 leading-relaxed" data-id="element-16">
      {children}
    </p>
  </div>;
const Card = ({
  title,
  children,
  icon: Icon
}: {
  title: string;
  children: React.ReactNode;
  icon?: any;
}) => <div className="bg-[#1a1f2e] border border-[#ffffff]/5 p-5 md:p-8 rounded-xl shadow-xl mb-4 md:mb-6" data-id="element-17">
    {Icon && <Icon className="w-6 h-6 md:w-8 md:h-8 text-[#c9a962] mb-3 md:mb-4" data-id="element-18" />}
    <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3 text-[#f5f0e8]" data-id="element-19">
      {title}
    </h3>
    <p className="text-sm md:text-base text-[#f5f0e8]/80 leading-relaxed" data-id="element-20">
      {children}
    </p>
  </div>;
const PrivacyModal = ({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-21">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} data-id="element-22" />
      <div className="relative bg-[#1a1f2e] rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto border border-[#ffffff]/10 shadow-2xl" data-id="element-23">
        <div className="sticky top-0 bg-[#1a1f2e] p-4 md:p-6 border-b border-[#ffffff]/10 flex justify-between items-center" data-id="element-24">
          <h2 className="text-xl md:text-2xl font-bold text-[#f5f0e8]" data-id="element-25">
            Privacy Policy
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-[#ffffff]/10 rounded-lg transition-colors" data-id="element-26">
            <X className="w-5 h-5 text-[#f5f0e8]/70" data-id="element-27" />
          </button>
        </div>
        <div className="p-4 md:p-6 text-[#f5f0e8]/80 text-sm md:text-base space-y-4" data-id="element-28">
          <p className="text-[#f5f0e8]/60 text-xs md:text-sm" data-id="element-29">
            Last updated: {new Date().toLocaleDateString()}
          </p>

          <h3 className="text-lg font-semibold text-[#f5f0e8] mt-6" data-id="element-30">
            1. Information We Collect
          </h3>
          <p data-id="element-31">
            When you purchase The Spectatoring Reset (50+), we collect only the
            information necessary to process your order:
          </p>
          <ul className="list-disc pl-5 space-y-1" data-id="element-32">
            <li data-id="element-33">Email address (for delivery of your PDF)</li>
            <li data-id="element-34">
              Payment information (processed securely through our payment
              provider)
            </li>
          </ul>
          <p data-id="element-35">
            We do not collect or store sensitive health information. Your
            purchase is completely private.
          </p>

          <h3 className="text-lg font-semibold text-[#f5f0e8] mt-6" data-id="element-36">
            2. How We Use Your Information
          </h3>
          <p data-id="element-37">Your information is used solely to:</p>
          <ul className="list-disc pl-5 space-y-1" data-id="element-38">
            <li data-id="element-39">Deliver your purchased digital product</li>
            <li data-id="element-40">Send order confirmation and receipt</li>
            <li data-id="element-41">Provide customer support if needed</li>
          </ul>

          <h3 className="text-lg font-semibold text-[#f5f0e8] mt-6" data-id="element-42">
            3. Your Privacy is Protected
          </h3>
          <p data-id="element-43">
            We understand the sensitive nature of this product. Your purchase
            will appear discreetly on billing statements. We never share, sell,
            or disclose your information to third parties for marketing
            purposes.
          </p>

          <h3 className="text-lg font-semibold text-[#f5f0e8] mt-6" data-id="element-44">
            4. Data Security
          </h3>
          <p data-id="element-45">
            All transactions are encrypted using industry-standard SSL
            technology. Payment processing is handled by trusted third-party
            providers who comply with PCI-DSS standards.
          </p>

          <h3 className="text-lg font-semibold text-[#f5f0e8] mt-6" data-id="element-46">
            5. Your Rights
          </h3>
          <p data-id="element-47">
            You may request deletion of your data at any time by contacting us.
            We will remove your information from our systems within 30 days of
            your request.
          </p>

          <h3 className="text-lg font-semibold text-[#f5f0e8] mt-6" data-id="element-48">
            6. Contact
          </h3>
          <p data-id="element-49">
            For any privacy-related questions or concerns, please contact our
            support team. We take your privacy seriously and will respond
            promptly to any inquiries.
          </p>

          <div className="pt-6 border-t border-[#ffffff]/10 mt-6" data-id="element-50">
            <Button onClick={onClose} className="w-full" data-id="element-51">
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>;
};
const TermsModal = ({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-52">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} data-id="element-53" />
      <div className="relative bg-[#1a1f2e] rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto border border-[#ffffff]/10 shadow-2xl" data-id="element-54">
        <div className="sticky top-0 bg-[#1a1f2e] p-4 md:p-6 border-b border-[#ffffff]/10 flex justify-between items-center" data-id="element-55">
          <h2 className="text-xl md:text-2xl font-bold text-[#f5f0e8]" data-id="element-56">
            Terms of Service
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-[#ffffff]/10 rounded-lg transition-colors" data-id="element-57">
            <X className="w-5 h-5 text-[#f5f0e8]/70" data-id="element-58" />
          </button>
        </div>
        <div className="p-4 md:p-6 text-[#f5f0e8]/80 text-sm md:text-base space-y-4" data-id="element-59">
          <p className="text-[#f5f0e8]/60 text-xs md:text-sm" data-id="element-60">
            Last updated: {new Date().toLocaleDateString()}
          </p>

          <h3 className="text-lg font-semibold text-[#f5f0e8] mt-6" data-id="element-61">
            1. Agreement to Terms
          </h3>
          <p data-id="element-62">
            By purchasing and accessing The Spectatoring Reset (50+), you agree
            to be bound by these Terms of Service. If you do not agree to these
            terms, please do not purchase or use this product.
          </p>

          <h3 className="text-lg font-semibold text-[#f5f0e8] mt-6" data-id="element-63">
            2. Product Description
          </h3>
          <p data-id="element-64">
            The Spectatoring Reset (50+) is a digital educational product
            delivered as a PDF document. It contains information, techniques,
            and exercises designed to help men manage performance anxiety. This
            product is for educational purposes only.
          </p>

          <h3 className="text-lg font-semibold text-[#f5f0e8] mt-6" data-id="element-65">
            3. Medical Disclaimer
          </h3>
          <p data-id="element-66">
            This product is NOT a substitute for professional medical advice,
            diagnosis, or treatment. The information provided is for educational
            purposes only and should not be considered medical advice.
          </p>
          <ul className="list-disc pl-5 space-y-1" data-id="element-67">
            <li data-id="element-68">
              Always consult with a qualified healthcare provider before
              starting any new health program
            </li>
            <li data-id="element-69">
              If you experience persistent erectile dysfunction, consult a
              medical professional
            </li>
            <li data-id="element-70">
              Do not disregard professional medical advice based on information
              in this guide
            </li>
            <li data-id="element-71">Individual results may vary</li>
          </ul>

          <h3 className="text-lg font-semibold text-[#f5f0e8] mt-6" data-id="element-72">
            4. Intellectual Property
          </h3>
          <p data-id="element-73">
            All content in The Spectatoring Reset (50+) is protected by
            copyright. You may not reproduce, distribute, modify, or create
            derivative works from this content without explicit written
            permission.
          </p>
          <ul className="list-disc pl-5 space-y-1" data-id="element-74">
            <li data-id="element-75">
              You receive a personal, non-transferable license to use this
              product
            </li>
            <li data-id="element-76">
              Sharing, reselling, or distributing the PDF is strictly prohibited
            </li>
            <li data-id="element-77">Screenshots or excerpts may not be shared publicly</li>
          </ul>

          <h3 className="text-lg font-semibold text-[#f5f0e8] mt-6" data-id="element-78">
            5. Refund Policy
          </h3>
          <p data-id="element-79">
            We offer a 7-day money-back guarantee. If you are not satisfied with
            your purchase for any reason, contact us within 7 days of purchase
            for a full refund. No questions asked.
          </p>
          <ul className="list-disc pl-5 space-y-1" data-id="element-80">
            <li data-id="element-81">Refund requests must be made within 7 days of purchase</li>
            <li data-id="element-82">Refunds are processed within 5-7 business days</li>
            <li data-id="element-83">Refunds are issued to the original payment method</li>
          </ul>

          <h3 className="text-lg font-semibold text-[#f5f0e8] mt-6" data-id="element-84">
            6. Limitation of Liability
          </h3>
          <p data-id="element-85">
            To the maximum extent permitted by law, we shall not be liable for
            any indirect, incidental, special, consequential, or punitive
            damages resulting from your use of this product. Our total liability
            shall not exceed the amount you paid for the product.
          </p>

          <h3 className="text-lg font-semibold text-[#f5f0e8] mt-6" data-id="element-86">
            7. No Guarantees
          </h3>
          <p data-id="element-87">
            While we believe in the effectiveness of the techniques presented,
            we make no guarantees regarding specific outcomes. Results depend on
            individual circumstances, commitment to the program, and various
            personal factors.
          </p>

          <h3 className="text-lg font-semibold text-[#f5f0e8] mt-6" data-id="element-88">
            8. Changes to Terms
          </h3>
          <p data-id="element-89">
            We reserve the right to modify these terms at any time. Continued
            use of the product after changes constitutes acceptance of the new
            terms.
          </p>

          <h3 className="text-lg font-semibold text-[#f5f0e8] mt-6" data-id="element-90">
            9. Contact Information
          </h3>
          <p data-id="element-91">
            For questions about these Terms of Service, please contact our
            support team. We aim to respond to all inquiries within 48 hours.
          </p>

          <div className="pt-6 border-t border-[#ffffff]/10 mt-6" data-id="element-92">
            <Button onClick={onClose} className="w-full" data-id="element-93">
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>;
};
// Scroll functions
const scrollToSection = (id: string) => {
  document.getElementById(id)?.scrollIntoView({
    behavior: 'smooth'
  });
};
const scrollToPricing = () => scrollToSection('pricing');
const goToCheckout = () => {
  window.location.href = CHECKOUT_URL;
};
// --- Floating Header ---
const FloatingHeader = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const navItems = [{
    label: 'Problem',
    id: 'problem'
  }, {
    label: 'Solution',
    id: 'solution'
  }, {
    label: 'What You Get',
    id: 'benefits'
  }, {
    label: 'Reviews',
    id: 'testimonials'
  }];
  const handleNavClick = (id: string) => {
    scrollToSection(id);
    setMobileMenuOpen(false);
  };
  return <>
      <motion.header initial={{
      y: -100
    }} animate={{
      y: 0
    }} transition={{
      duration: 0.5,
      ease: 'easeOut'
    }} className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#0f1419]/95 backdrop-blur-md border-b border-[#ffffff]/5 shadow-lg' : 'bg-transparent'}`} data-id="element-94">
        <div className="max-w-5xl mx-auto px-4 md:px-6" data-id="element-95">
          <div className="flex items-center justify-between h-14 md:h-16" data-id="element-96">
            {/* Logo */}
            <button onClick={() => window.scrollTo({
            top: 0,
            behavior: 'smooth'
          })} className="flex items-center gap-2 group" data-id="element-97">
              <div className="w-8 h-8 rounded-lg bg-[#c9a962]/20 flex items-center justify-center" data-id="element-98">
                <span className="text-[#c9a962] font-bold text-sm" data-id="element-99">SR</span>
              </div>
              <span className="hidden sm:block text-[#f5f0e8] font-semibold text-sm group-hover:text-[#c9a962] transition-colors" data-id="element-100">
                Spectatoring Reset
              </span>
            </button>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1" data-id="element-101">
              {navItems.map(item => <button key={item.id} onClick={() => handleNavClick(item.id)} className="px-3 py-2 text-sm text-[#f5f0e8]/70 hover:text-[#c9a962] transition-colors rounded-lg hover:bg-[#ffffff]/5" data-id="element-102">
                  {item.label}
                </button>)}
            </nav>

            {/* CTA Button */}
            <div className="flex items-center gap-3" data-id="element-103">
              <button onClick={scrollToPricing} className="hidden sm:flex flex-col items-center px-4 py-2 bg-[#c9a962] text-[#0f1419] rounded-lg text-sm font-semibold hover:bg-[#d4b46e] transition-all shadow-lg shadow-[#c9a962]/20" data-id="element-104">
                <span data-id="element-105">PDF + Audio — $17</span>
              </button>

              {/* Mobile Menu Button */}
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 text-[#f5f0e8]/70 hover:text-[#c9a962] hover:bg-[#ffffff]/5 rounded-lg transition-colors" data-id="element-106">
                {mobileMenuOpen ? <X className="w-5 h-5" data-id="element-107" /> : <Menu className="w-5 h-5" data-id="element-108" />}
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence data-id="element-109">
        {mobileMenuOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-40 md:hidden" data-id="element-110">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)} data-id="element-111" />
            <motion.div initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          duration: 0.3,
          ease: 'easeOut'
        }} className="absolute right-0 top-0 bottom-0 w-64 bg-[#1a1f2e] border-l border-[#ffffff]/10 shadow-2xl" data-id="element-112">
              <div className="p-4 pt-20" data-id="element-113">
                <nav className="space-y-1" data-id="element-114">
                  {navItems.map(item => <button key={item.id} onClick={() => handleNavClick(item.id)} className="w-full text-left px-4 py-3 text-[#f5f0e8]/80 hover:text-[#c9a962] hover:bg-[#ffffff]/5 rounded-lg transition-colors" data-id="element-115">
                      {item.label}
                    </button>)}
                </nav>
                <div className="mt-6 pt-6 border-t border-[#ffffff]/10" data-id="element-116">
                  <button onClick={() => {
                scrollToPricing();
                setMobileMenuOpen(false);
              }} className="w-full px-4 py-3 bg-[#c9a962] text-[#0f1419] rounded-lg font-semibold hover:bg-[#d4b46e] transition-all text-center" data-id="element-117">
                    <span className="block" data-id="element-118">PDF + Audio Book</span>
                    <span className="block text-xs opacity-80" data-id="element-119">Only $17</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
};
// --- Audio Preview Player ---
const AudioPreviewPlayer = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  // NOTE: This URL needs to be a DIRECT link to an audio file (mp3, wav, etc.)
  // File sharing URLs like share.deal.ai typically don't work - you need the actual file URL
  const audioUrl = 'https://share.deal.ai/files/69813d343d8add5bb3094693/2bf76b07-861f-4092-98cf-4a1654c677b7';
  const togglePlay = async () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        setIsLoading(true);
        setHasError(false);
        try {
          await audioRef.current.play();
          setIsPlaying(true);
        } catch (err) {
          console.error('Playback failed:', err);
          setHasError(true);
        }
        setIsLoading(false);
      }
    }
  };
  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };
  const handleLoadedMetadata = () => {
    if (audioRef.current && audioRef.current.duration) {
      setDuration(audioRef.current.duration);
      setHasError(false);
    }
  };
  const handleError = () => {
    console.error('Audio failed to load - URL may not be a direct audio file');
    setHasError(true);
    setIsLoading(false);
  };
  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };
  const formatTime = (time: number) => {
    if (isNaN(time) || !isFinite(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };
  const progress = duration > 0 ? currentTime / duration * 100 : 0;
  // Show native player as fallback if there's an error
  if (hasError) {
    return <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.3
    }} className="mt-4" data-id="element-120">
        <div className="bg-gradient-to-br from-[#1a1f2e]/95 to-[#0f1419]/95 backdrop-blur-xl rounded-2xl p-5 border border-[#c9a962]/30 shadow-2xl shadow-black/20" data-id="element-121">
          <div className="flex items-center gap-3 mb-4" data-id="element-122">
            <div className="w-10 h-10 rounded-full bg-[#c9a962]/20 flex items-center justify-center" data-id="element-123">
              <Headphones className="w-5 h-5 text-[#c9a962]" data-id="element-124" />
            </div>
            <div data-id="element-125">
              <p className="text-[#f5f0e8] font-semibold text-sm" data-id="element-126">
                Audio Book Preview
              </p>
              <p className="text-[#f5f0e8]/50 text-xs" data-id="element-127">Use the player below</p>
            </div>
          </div>

          {/* Native HTML5 audio player */}
          <audio controls className="w-full h-12 rounded-lg" style={{
          filter: 'brightness(0.9) contrast(1.1)'
        }} data-id="element-128">
            <source src={audioUrl} type="audio/mpeg" data-id="element-129" />
            <source src={audioUrl} type="audio/mp4" data-id="element-130" />
            <source src={audioUrl} type="audio/wav" data-id="element-131" />
            <source src={audioUrl} type="audio/ogg" data-id="element-132" />
            Your browser does not support audio playback.
          </audio>

          <p className="text-[#f5f0e8]/40 text-xs mt-3 text-center" data-id="element-133">
            Full audio book included with purchase
          </p>
        </div>
      </motion.div>;
  }
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: 0.3
  }} className="mt-4 relative overflow-hidden" data-id="element-134">
      {/* Glassmorphism container */}
      <div className="bg-gradient-to-br from-[#1a1f2e]/95 to-[#0f1419]/95 backdrop-blur-xl rounded-2xl p-5 border border-[#c9a962]/30 shadow-2xl shadow-black/20" data-id="element-135">
        <audio ref={audioRef} src={audioUrl} preload="auto" crossOrigin="anonymous" onTimeUpdate={handleTimeUpdate} onLoadedMetadata={handleLoadedMetadata} onEnded={() => setIsPlaying(false)} onPause={() => setIsPlaying(false)} onPlay={() => setIsPlaying(true)} onError={handleError} onCanPlayThrough={() => setHasError(false)} data-id="element-136" />

        {/* Decorative glow */}
        <div className="absolute -top-20 -right-20 w-40 h-40 bg-[#c9a962]/10 rounded-full blur-3xl pointer-events-none" data-id="element-137" />

        {/* Header with waveform visual */}
        <div className="flex items-center gap-4 mb-4" data-id="element-138">
          {/* Animated play button */}
          <motion.button onClick={togglePlay} disabled={isLoading} whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} className="relative w-14 h-14 rounded-full bg-gradient-to-br from-[#c9a962] to-[#a8893f] flex items-center justify-center shadow-lg shadow-[#c9a962]/30 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-139">
            {/* Pulse ring when playing */}
            {isPlaying && <motion.div className="absolute inset-0 rounded-full border-2 border-[#c9a962]" animate={{
            scale: [1, 1.3],
            opacity: [0.5, 0]
          }} transition={{
            duration: 1.5,
            repeat: Infinity
          }} data-id="element-140" />}
            {isLoading ? <div className="w-6 h-6 border-2 border-[#0f1419] border-t-transparent rounded-full animate-spin" data-id="element-141" /> : isPlaying ? <Pause className="w-6 h-6 text-[#0f1419]" data-id="element-142" /> : <Play className="w-6 h-6 text-[#0f1419] ml-1" data-id="element-143" />}
          </motion.button>

          <div className="flex-1" data-id="element-144">
            <div className="flex items-center gap-2" data-id="element-145">
              <Headphones className="w-4 h-4 text-[#c9a962]" data-id="element-146" />
              <p className="text-[#f5f0e8] font-semibold text-sm" data-id="element-147">
                Audio Book Preview
              </p>
            </div>
            <p className="text-[#f5f0e8]/50 text-xs mt-0.5" data-id="element-148">
              Listen before you buy
            </p>
          </div>

          {/* Sound wave animation */}
          <div className="flex items-center gap-0.5 h-8" data-id="element-149">
            {[...Array(5)].map((_, i) => <motion.div key={i} className="w-1 bg-[#c9a962] rounded-full" animate={isPlaying ? {
            height: [8, 20, 12, 24, 8]
          } : {
            height: 8
          }} transition={{
            duration: 0.8,
            repeat: Infinity,
            delay: i * 0.1,
            ease: 'easeInOut'
          }} data-id="element-150" />)}
          </div>
        </div>

        {/* Progress section */}
        <div className="space-y-2" data-id="element-151">
          {/* Custom progress bar */}
          <div className="relative h-2 bg-[#ffffff]/10 rounded-full overflow-hidden" data-id="element-152">
            <motion.div className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#c9a962] to-[#d4b46e] rounded-full" style={{
            width: `${progress}%`
          }} data-id="element-153" />
            <input type="range" min="0" max={duration || 100} value={currentTime} onChange={handleSeek} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" data-id="element-154" />
            {/* Thumb indicator */}
            <motion.div className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-lg" style={{
            left: `calc(${progress}% - 6px)`
          }} data-id="element-155" />
          </div>

          {/* Time display */}
          <div className="flex justify-between items-center" data-id="element-156">
            <span className="text-[#f5f0e8]/60 text-xs font-mono" data-id="element-157">
              {formatTime(currentTime)}
            </span>
            <div className="flex items-center gap-1.5" data-id="element-158">
              <Volume2 className="w-3 h-3 text-[#f5f0e8]/40" data-id="element-159" />
              <span className="text-[#f5f0e8]/40 text-xs" data-id="element-160">Preview</span>
            </div>
            <span className="text-[#f5f0e8]/60 text-xs font-mono" data-id="element-161">
              {formatTime(duration)}
            </span>
          </div>
        </div>
      </div>
    </motion.div>;
};
// --- Facebook-style Comments Section ---
// Profile pictures for men 50+ demographic - natural, casual photos
const profilePics: {
  [key: string]: string;
} = {
  'Michael R.': 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face',
  'James K.': 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face',
  'David M.': 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop&crop=face',
  'Robert T.': 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=100&h=100&fit=crop&crop=face',
  'Steve H.': 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=100&h=100&fit=crop&crop=face',
  'Paul W.': 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100&h=100&fit=crop&crop=face',
  'Thomas B.': 'https://images.unsplash.com/photo-1504257432389-52343af06ae3?w=100&h=100&fit=crop&crop=face',
  'William C.': 'https://images.unsplash.com/photo-1463453091185-61582044d556?w=100&h=100&fit=crop&crop=face',
  'George F.': 'https://images.unsplash.com/photo-1552058544-f2b08422138a?w=100&h=100&fit=crop&crop=face',
  'Richard P.': 'https://images.unsplash.com/photo-1548372290-8d01b6c8e78c?w=100&h=100&fit=crop&crop=face',
  'Charles D.': 'https://images.unsplash.com/photo-1566492031773-4f4e44671857?w=100&h=100&fit=crop&crop=face',
  'Frank L.': 'https://images.unsplash.com/photo-1557862921-37829c790f19?w=100&h=100&fit=crop&crop=face',
  'Joseph M.': 'https://images.unsplash.com/photo-1492562080023-ab3db95bfbce?w=100&h=100&fit=crop&crop=face',
  'Daniel K.': 'https://images.unsplash.com/photo-1568602471122-7832951cc4c5?w=100&h=100&fit=crop&crop=face',
  'Mark S.': 'https://images.unsplash.com/photo-1564564321837-a57b7070ac4f?w=100&h=100&fit=crop&crop=face',
  'Kevin R.': 'https://images.unsplash.com/photo-1480429370612-2f1e0c913e7c?w=100&h=100&fit=crop&crop=face',
  'Anthony G.': 'https://images.unsplash.com/photo-1545167622-3a6ac756afa4?w=100&h=100&fit=crop&crop=face',
  'Donald H.': 'https://images.unsplash.com/photo-1474176857210-7287d38d27c6?w=100&h=100&fit=crop&crop=face',
  'Steven J.': 'https://images.unsplash.com/photo-1508341591423-4347099e1f19?w=100&h=100&fit=crop&crop=face',
  'Brian T.': 'https://images.unsplash.com/photo-1507591064344-4c6ce005b128?w=100&h=100&fit=crop&crop=face',
  'Andrew L.': 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face&sat=-100',
  'Kenneth N.': 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop&crop=face&sat=-50',
  'Joshua P.': 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100&h=100&fit=crop&crop=face&bri=10',
  'Ryan Q.': 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=100&h=100&fit=crop&crop=face&con=10',
  'Eric S.': 'https://images.unsplash.com/photo-1504257432389-52343af06ae3?w=100&h=100&fit=crop&crop=face&bri=-10',
  'Gregory U.': 'https://images.unsplash.com/photo-1463453091185-61582044d556?w=100&h=100&fit=crop&crop=face&sat=-30',
  'Edward V.': 'https://images.unsplash.com/photo-1552058544-f2b08422138a?w=100&h=100&fit=crop&crop=face&con=-10',
  'Ronald W.': 'https://images.unsplash.com/photo-1548372290-8d01b6c8e78c?w=100&h=100&fit=crop&crop=face&bri=5',
  'Timothy X.': 'https://images.unsplash.com/photo-1566492031773-4f4e44671857?w=100&h=100&fit=crop&crop=face&sat=-20',
  'Larry Y.': 'https://images.unsplash.com/photo-1557862921-37829c790f19?w=100&h=100&fit=crop&crop=face&bri=-5',
  'Jason Z.': 'https://images.unsplash.com/photo-1492562080023-ab3db95bfbce?w=100&h=100&fit=crop&crop=face&con=5',
  'Jeffrey A.': 'https://images.unsplash.com/photo-1568602471122-7832951cc4c5?w=100&h=100&fit=crop&crop=face&sat=-40',
  'Gary B.': 'https://images.unsplash.com/photo-1564564321837-a57b7070ac4f?w=100&h=100&fit=crop&crop=face&bri=10',
  'Nicholas C.': 'https://images.unsplash.com/photo-1480429370612-2f1e0c913e7c?w=100&h=100&fit=crop&crop=face&sat=-10',
  'Scott D.': 'https://images.unsplash.com/photo-1545167622-3a6ac756afa4?w=100&h=100&fit=crop&crop=face&con=-5',
  'Raymond E.': 'https://images.unsplash.com/photo-1474176857210-7287d38d27c6?w=100&h=100&fit=crop&crop=face&bri=-10',
  'Samuel F.': 'https://images.unsplash.com/photo-1508341591423-4347099e1f19?w=100&h=100&fit=crop&crop=face&sat=-60',
  'Patrick G.': 'https://images.unsplash.com/photo-1507591064344-4c6ce005b128?w=100&h=100&fit=crop&crop=face&con=10',
  'Jack H.': 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face&bri=5',
  'Dennis I.': 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=100&h=100&fit=crop&crop=face&sat=-25',
  'Jerry J.': 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face&bri=-5',
  'Walter K.': 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop&crop=face&con=-10',
  'Peter L.': 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100&h=100&fit=crop&crop=face&sat=-35',
  'Harold M.': 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=100&h=100&fit=crop&crop=face&bri=10',
  'Carl N.': 'https://images.unsplash.com/photo-1504257432389-52343af06ae3?w=100&h=100&fit=crop&crop=face&sat=-15',
  'Arthur O.': 'https://images.unsplash.com/photo-1463453091185-61582044d556?w=100&h=100&fit=crop&crop=face&con=5'
};
const facebookComments = [{
  id: 1,
  name: 'Michael R.',
  initial: 'M',
  time: '2d',
  likes: 47,
  comment: "Just finished day 5. The downshift technique alone is worth 10x the price. Used it last night and for the first time in months I wasn't in my head the whole time.",
  replies: [{
    id: 101,
    name: 'James K.',
    initial: 'J',
    time: '1d',
    likes: 12,
    comment: 'Same here brother. Night and day difference.'
  }, {
    id: 102,
    name: 'David M.',
    initial: 'D',
    time: '1d',
    likes: 8,
    comment: 'The re-anchor part clicked for me on day 3. Game changer.'
  }]
}, {
  id: 2,
  name: 'Robert T.',
  initial: 'R',
  time: '1w',
  likes: 89,
  comment: "56 years old, divorced 2 years ago. Started dating again and the anxiety was killing me. This guide gave me an actual SYSTEM instead of just 'relax bro'. My confidence is back.",
  replies: []
}, {
  id: 3,
  name: 'Steve H.',
  initial: 'S',
  time: '5d',
  likes: 34,
  comment: "The part about reading her face like a verdict... felt like someone was reading my mind. Finally understand what's been happening.",
  replies: [{
    id: 103,
    name: 'Paul W.',
    initial: 'P',
    time: '4d',
    likes: 15,
    comment: "That section hit different. So accurate it's scary."
  }]
}, {
  id: 4,
  name: 'Thomas B.',
  initial: 'T',
  time: '3d',
  likes: 52,
  comment: 'Skeptical at first but the 90-second reset actually works. Wife noticed the difference immediately. Less rushing, more presence.',
  replies: []
}, {
  id: 5,
  name: 'William C.',
  initial: 'W',
  time: '6d',
  likes: 28,
  comment: "The no-fail ladder removed so much pressure. Even when things weren't perfect, I didn't spiral. That's huge for me.",
  replies: [{
    id: 104,
    name: 'George F.',
    initial: 'G',
    time: '5d',
    likes: 9,
    comment: 'This ^ The ladder concept changed everything'
  }]
}, {
  id: 6,
  name: 'Richard P.',
  initial: 'R',
  time: '4d',
  likes: 41,
  comment: "53, been dealing with this for 3 years. Tried everything. This is the first thing that addresses the MENTAL side properly. Not just 'think positive'.",
  replies: []
}, {
  id: 7,
  name: 'Charles D.',
  initial: 'C',
  time: '1w',
  likes: 63,
  comment: "The pre-game script saved my relationship. Finally had a conversation with my partner that didn't feel like a confession or excuse.",
  replies: [{
    id: 105,
    name: 'Frank L.',
    initial: 'F',
    time: '6d',
    likes: 22,
    comment: 'How did that conversation go? Thinking about having it myself'
  }, {
    id: 106,
    name: 'Charles D.',
    initial: 'C',
    time: '6d',
    likes: 31,
    comment: "Way better than expected. She was relieved I wasn't pulling away because of her. The script in the guide is perfect - not too much, not too little."
  }]
}, {
  id: 8,
  name: 'Joseph M.',
  initial: 'J',
  time: '2d',
  likes: 19,
  comment: 'Day 7 check in. The monitoring thing is SO real. Never realized how much I was doing it until I learned to catch it.',
  replies: []
}, {
  id: 9,
  name: 'Daniel K.',
  initial: 'D',
  time: '5d',
  likes: 37,
  comment: "Best $17 I've spent. Period. Should be charging way more for this.",
  replies: []
}, {
  id: 10,
  name: 'Mark S.',
  initial: 'M',
  time: '3d',
  likes: 44,
  comment: "The relapse-proofing section is clutch. Had a rough night on day 4 but didn't let it spiral into weeks of avoidance like before.",
  replies: [{
    id: 107,
    name: 'Kevin R.',
    initial: 'K',
    time: '2d',
    likes: 16,
    comment: "That's the part most guides miss. What to do AFTER a bad night."
  }]
}, {
  id: 11,
  name: 'Anthony G.',
  initial: 'A',
  time: '1w',
  likes: 55,
  comment: '58 here. Widower, started dating last year. The anxiety was unbearable. This guide gets it. Written by someone who actually understands.',
  replies: []
}, {
  id: 12,
  name: 'Donald H.',
  initial: 'D',
  time: '4d',
  likes: 23,
  comment: "Finally something that doesn't make me feel broken. The framing in this guide is respectful and practical.",
  replies: []
}, {
  id: 13,
  name: 'Steven J.',
  initial: 'S',
  time: '6d',
  likes: 38,
  comment: 'The checklist for the night-of is brilliant. Having a plan removes so much uncertainty.',
  replies: [{
    id: 108,
    name: 'Brian T.',
    initial: 'B',
    time: '5d',
    likes: 11,
    comment: 'Agreed. I actually look forward to dates now instead of dreading them'
  }]
}, {
  id: 14,
  name: 'Andrew L.',
  initial: 'A',
  time: '2d',
  likes: 29,
  comment: 'Showed this to my therapist (yes I see one for anxiety). She said the nervous system approach is spot on. Wish more resources took this angle.',
  replies: []
}, {
  id: 15,
  name: 'Kenneth N.',
  initial: 'K',
  time: '5d',
  likes: 46,
  comment: "3 weeks in. The spiral hasn't happened once. Used to be every single time. This stuff works if you actually do it.",
  replies: []
}, {
  id: 16,
  name: 'Joshua P.',
  initial: 'J',
  time: '1w',
  likes: 71,
  comment: "Bought this after my 3rd failed attempt with someone I really liked. Followed the protocol. 4th time was completely different. We're still together 👍",
  replies: [{
    id: 109,
    name: 'Ryan Q.',
    initial: 'R',
    time: '6d',
    likes: 24,
    comment: 'Love to hear it man. Congrats!'
  }, {
    id: 110,
    name: 'Eric S.',
    initial: 'E',
    time: '5d',
    likes: 18,
    comment: 'This gives me hope. Thanks for sharing.'
  }]
}, {
  id: 17,
  name: 'Gregory U.',
  initial: 'G',
  time: '3d',
  likes: 32,
  comment: 'The attention strategy (re-anchor) is simple but powerful. Pulled me out of my head multiple times already.',
  replies: []
}, {
  id: 18,
  name: 'Edward V.',
  initial: 'E',
  time: '4d',
  likes: 26,
  comment: '54, post-divorce. This guide understands the specific pressure of dating again at this age. Not generic advice.',
  replies: []
}, {
  id: 19,
  name: 'Ronald W.',
  initial: 'R',
  time: '2d',
  likes: 21,
  comment: 'Day 10. The daily exercises are quick but effective. 5-10 mins max. Easy to stick with.',
  replies: []
}, {
  id: 20,
  name: 'Timothy X.',
  initial: 'T',
  time: '1w',
  likes: 58,
  comment: 'Tried pills before. They helped physically but made the mental pressure worse. This addresses the root cause.',
  replies: [{
    id: 111,
    name: 'Larry Y.',
    initial: 'L',
    time: '6d',
    likes: 19,
    comment: 'Exactly my experience. Pills + this guide together = finally feeling normal again'
  }]
}, {
  id: 21,
  name: 'Jason Z.',
  initial: 'J',
  time: '5d',
  likes: 35,
  comment: "The 'surveillance camera' metaphor in the intro... that's exactly what it feels like. Finally someone put it into words.",
  replies: []
}, {
  id: 22,
  name: 'Jeffrey A.',
  initial: 'J',
  time: '3d',
  likes: 27,
  comment: 'Discrete purchase, instant download, actually helpful content. Everything it promised.',
  replies: []
}, {
  id: 23,
  name: 'Gary B.',
  initial: 'G',
  time: '6d',
  likes: 42,
  comment: "My girlfriend thinks I've been 'more present' lately. She doesn't know about the guide but she notices the difference.",
  replies: []
}, {
  id: 24,
  name: 'Nicholas C.',
  initial: 'N',
  time: '4d',
  likes: 31,
  comment: 'The 14-day structure is perfect. Not overwhelming, builds on itself. Day 14 felt like a graduation.',
  replies: [{
    id: 112,
    name: 'Scott D.',
    initial: 'S',
    time: '3d',
    likes: 13,
    comment: 'Just hit day 14 yesterday. Totally agree. Feel like a different person.'
  }]
}, {
  id: 25,
  name: 'Raymond E.',
  initial: 'R',
  time: '2d',
  likes: 24,
  comment: '51 here. Wish I had this 5 years ago. Would have saved a lot of shame and confusion.',
  replies: []
}, {
  id: 26,
  name: 'Samuel F.',
  initial: 'S',
  time: '1w',
  likes: 49,
  comment: "The part about 'trying harder makes you monitor harder' was a lightbulb moment. I was making it worse without knowing.",
  replies: []
}, {
  id: 27,
  name: 'Patrick G.',
  initial: 'P',
  time: '5d',
  likes: 33,
  comment: 'Clean, practical, no BS. Exactly what I needed. Not some guru trying to sell me a lifestyle.',
  replies: []
}, {
  id: 28,
  name: 'Jack H.',
  initial: 'J',
  time: '3d',
  likes: 28,
  comment: 'The guarantee made me try it. The results made me keep it. No regrets.',
  replies: []
}, {
  id: 29,
  name: 'Dennis I.',
  initial: 'D',
  time: '4d',
  likes: 36,
  comment: "Used the downshift technique before a date last week. Completely different energy. She even commented that I seemed 'calm and confident'. 😂",
  replies: [{
    id: 113,
    name: 'Jerry J.',
    initial: 'J',
    time: '3d',
    likes: 14,
    comment: "That's the goal right there! Nice work"
  }]
}, {
  id: 30,
  name: 'Walter K.',
  initial: 'W',
  time: '6d',
  likes: 39,
  comment: "57, remarried. The pressure to 'perform' for a new partner was crushing. This guide reframed everything. Much healthier mindset now.",
  replies: []
}, {
  id: 31,
  name: 'Peter L.',
  initial: 'P',
  time: '2d',
  likes: 22,
  comment: 'Short, actionable, private. Three things I needed. Delivered on all three.',
  replies: []
}, {
  id: 32,
  name: 'Harold M.',
  initial: 'H',
  time: '1w',
  likes: 67,
  comment: "Bought this 3 weeks ago. Life changing isn't an exaggeration. My only regret is not finding it sooner. To anyone on the fence - just get it.",
  replies: [{
    id: 114,
    name: 'Carl N.',
    initial: 'C',
    time: '6d',
    likes: 21,
    comment: 'Second this. Stop overthinking and just try it.'
  }, {
    id: 115,
    name: 'Arthur O.',
    initial: 'A',
    time: '5d',
    likes: 17,
    comment: 'Agreed 100%. Best decision I made this year.'
  }]
}];
const FacebookComment = ({
  comment,
  isReply = false
}: {
  comment: any;
  isReply?: boolean;
}) => {
  const [liked, setLiked] = useState(false);
  const profilePic = profilePics[comment.name];
  return <div className={`flex gap-2 ${isReply ? 'ml-10 mt-2' : 'mb-3'}`} data-id="element-162">
      {profilePic ? <img src={profilePic} alt={comment.name} className={`${isReply ? 'w-6 h-6' : 'w-8 h-8'} rounded-full object-cover flex-shrink-0`} data-id="element-163" /> : <div className={`${isReply ? 'w-6 h-6' : 'w-8 h-8'} rounded-full bg-gray-400 flex items-center justify-center text-white text-xs font-bold flex-shrink-0`} data-id="element-164">
          {comment.initial}
        </div>}
      <div className="flex-1" data-id="element-165">
        <div className="bg-[#f0f2f5] rounded-2xl px-3 py-2 inline-block" data-id="element-166">
          <p className="font-semibold text-[#050505] text-sm" data-id="element-167">{comment.name}</p>
          <p className="text-[#050505] text-sm" data-id="element-168">{comment.comment}</p>
        </div>
        <div className="flex items-center gap-4 mt-1 ml-2 text-xs text-[#65676b]" data-id="element-169">
          <span data-id="element-170">{comment.time}</span>
          <button onClick={() => setLiked(!liked)} className={`font-semibold hover:underline ${liked ? 'text-[#1877f2]' : ''}`} data-id="element-171">
            Like
          </button>
          <button className="font-semibold hover:underline" data-id="element-172">Reply</button>
          {comment.likes > 0 && <span className="flex items-center gap-1" data-id="element-173">
              <span className="bg-[#1877f2] rounded-full p-0.5" data-id="element-174">
                <ThumbsUp className="w-2.5 h-2.5 text-white fill-white" data-id="element-175" />
              </span>
              {liked ? comment.likes + 1 : comment.likes}
            </span>}
        </div>
        {comment.replies && comment.replies.map((reply: any) => <FacebookComment key={reply.id} comment={reply} isReply data-id="element-176" />)}
      </div>
    </div>;
};
const TestimonialsSection = () => {
  const [showAll, setShowAll] = useState(false);
  const displayedComments = showAll ? facebookComments : facebookComments.slice(0, 8);
  return <Section className="relative z-10" data-id="element-177">
      <h2 className="text-2xl md:text-4xl font-bold mb-6 md:mb-8 text-center" data-id="element-178">
        What men are saying
      </h2>

      {/* Facebook-style container */}
      <div className="bg-white rounded-lg shadow-xl overflow-hidden max-w-2xl mx-auto" data-id="element-179">
        {/* Facebook header */}
        <div className="bg-[#1877f2] px-4 py-3 flex items-center justify-between" data-id="element-180">
          <div className="flex items-center gap-2" data-id="element-181">
            <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor" data-id="element-182">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" data-id="element-183" />
            </svg>
            <span className="text-white font-semibold text-sm" data-id="element-184">
              Comments from our community
            </span>
          </div>
          <span className="text-white/80 text-xs" data-id="element-185">
            {facebookComments.length} comments
          </span>
        </div>

        {/* Comments area */}
        <div className="p-4 max-h-[600px] overflow-y-auto" data-id="element-186">
          {displayedComments.map(comment => <FacebookComment key={comment.id} comment={comment} data-id="element-187" />)}
        </div>

        {/* Show more button */}
        {!showAll && <div className="px-4 pb-4" data-id="element-188">
            <button onClick={() => setShowAll(true)} className="w-full py-2 text-[#1877f2] font-semibold text-sm hover:bg-[#f0f2f5] rounded-lg transition-colors" data-id="element-189">
              View all {facebookComments.length} comments
            </button>
          </div>}
      </div>

      <p className="text-center mt-4 text-xs text-[#f5f0e8]/40" data-id="element-190">
        Real feedback from verified purchasers. Names shortened for privacy.
      </p>
    </Section>;
};
// --- Bonus Card Component ---
const BonusCard = ({
  image,
  title,
  subtitle,
  value,
  number
}: {
  image: string;
  title: string;
  subtitle: string;
  value: string;
  number: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true
}} transition={{
  delay: number * 0.2
}} className="relative bg-gradient-to-br from-[#1a1f2e] to-[#0f1419] rounded-2xl overflow-hidden border border-[#c9a962]/30 shadow-2xl" data-id="element-191">
    {/* FREE Badge */}
    <div className="absolute top-4 right-4 z-10" data-id="element-192">
      <span className="px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full shadow-lg" data-id="element-193">
        FREE BONUS
      </span>
    </div>

    {/* Bonus Number */}
    <div className="absolute top-4 left-4 z-10" data-id="element-194">
      <div className="w-8 h-8 rounded-full bg-[#c9a962] flex items-center justify-center" data-id="element-195">
        <span className="text-[#0f1419] font-bold text-sm" data-id="element-196">#{number}</span>
      </div>
    </div>

    {/* Image */}
    <div className="aspect-[4/3] overflow-hidden" data-id="element-197">
      <img src={image} alt={title} className="w-full h-full object-cover" data-id="element-198" />
    </div>

    {/* Content */}
    <div className="p-5" data-id="element-199">
      <div className="flex items-center justify-between mb-2" data-id="element-200">
        <h3 className="text-lg font-bold text-[#f5f0e8]" data-id="element-201">{title}</h3>
        <span className="text-[#c9a962] font-bold text-sm" data-id="element-202">{value} Value</span>
      </div>
      <p className="text-[#f5f0e8]/70 text-sm" data-id="element-203">{subtitle}</p>
    </div>
  </motion.div>;
// --- 7-Day Guarantee Badge ---
const GuaranteeBadge = () => <div className="flex items-center justify-center gap-3 mt-6 p-4 bg-[#0a0d12] rounded-xl border border-[#c9a962]/20" data-id="element-204">
    <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-[#c9a962]/20 flex items-center justify-center flex-shrink-0" data-id="element-205">
      <ShieldCheck className="w-6 h-6 md:w-7 md:h-7 text-[#c9a962]" data-id="element-206" />
    </div>
    <div className="text-left" data-id="element-207">
      <p className="text-[#c9a962] font-bold text-sm md:text-base" data-id="element-208">
        7-Day Money-Back Guarantee
      </p>
      <p className="text-[#f5f0e8]/60 text-xs md:text-sm" data-id="element-209">
        Try it risk-free. If it's not for you, get a full refund. No questions
        asked.
      </p>
    </div>
  </div>;
// Bonus images
const BONUS_1_IMAGE = 'https://cdn.magicpatterns.com/uploads/f1CjkZC1u5W642Cf9oEN5w/Bono1.png';
const BONUS_2_IMAGE = 'https://cdn.magicpatterns.com/uploads/9xdEem4pz7fSF5UBFbNiTq/bono_2.png';
// --- Main Page ---
export function SalesPage() {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const newHeroImage = 'https://cdn.magicpatterns.com/uploads/7Jc5ymeZi45Hzuuvb1J3Qx/heylloyd-image-1770077780191.png';
  return <div className="min-h-screen bg-[#0f1419] text-[#f5f0e8] overflow-x-hidden relative" data-id="element-210">
      {/* Floating Header */}
      <FloatingHeader data-id="element-211" />

      {/* Global Background Waves */}
      <div className="fixed inset-0 pointer-events-none z-0" data-id="element-212">
        <AnimatedWaves data-id="element-213" />
        <PulseGlow className="w-96 h-96 -top-48 -right-48" data-id="element-214" />
        <PulseGlow className="w-80 h-80 top-1/3 -left-40" data-id="element-215" />
        <PulseGlow className="w-64 h-64 bottom-1/4 right-1/4" data-id="element-216" />
      </div>

      {/* SECTION 1: HERO - Single column layout for both mobile and desktop */}
      <Section className="pt-20 pb-8 md:pt-28 md:pb-16 relative z-10" data-id="element-217">
        <div className="text-center mb-8 md:mb-12" data-id="element-218">
          <span className="inline-block px-3 py-1 mb-4 md:mb-6 text-xs font-semibold tracking-widest text-[#c9a962] uppercase bg-[#c9a962]/10 rounded-full" data-id="element-219">
            For Men 50+
          </span>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold leading-tight mb-4 md:mb-6 text-[#f5f0e8]" data-id="element-220">
            You're Not "Broken." <br data-id="element-221" />
            <span className="text-[#c9a962]" data-id="element-222">You're Monitoring Yourself.</span>
          </h1>
          <p className="text-lg md:text-xl lg:text-2xl text-[#f5f0e8]/80 max-w-2xl mx-auto font-light leading-relaxed" data-id="element-223">
            The 90-Second Reset for the Moment You Start Panicking
          </p>
        </div>

        {/* Hero image - visible on all screen sizes */}
        <div className="relative max-w-lg mx-auto mb-8 md:mb-12" data-id="element-224">
          <div className="absolute inset-0 bg-[#c9a962]/20 blur-3xl rounded-full opacity-20" data-id="element-225"></div>
          <img src={newHeroImage} alt="The Spectatoring Reset Guide - Audio Book on tablet" className="relative z-10 rounded-2xl shadow-2xl border border-[#ffffff]/10 w-full object-cover" data-id="element-226" />
          {/* Audio Player */}
          <AudioPreviewPlayer data-id="element-227" />
        </div>

        {/* Text content - centered single column */}
        <div className="max-w-2xl mx-auto space-y-4 md:space-y-6" data-id="element-228">
          <p className="text-base md:text-lg leading-relaxed text-[#f5f0e8]/90" data-id="element-229">
            You're not failing because you're 50+. You're failing because, at
            the exact moment intimacy starts, your brain turns into a
            surveillance camera.
          </p>
          <p className="text-base md:text-lg leading-relaxed text-[#f5f0e8]/90" data-id="element-230">
            You start checking.
            <br data-id="element-231" />
            You start guessing what she's thinking.
            <br data-id="element-232" />
            You start trying to "make it happen."
          </p>
          <p className="text-base md:text-lg font-semibold text-[#f5f0e8]" data-id="element-233">
            And the checking kills it.
          </p>
          <p className="text-base md:text-lg leading-relaxed text-[#f5f0e8]/90" data-id="element-234">
            <strong className="text-[#c9a962]" data-id="element-235">
              The Spectatoring Reset (50+)
            </strong>{' '}
            is a private 14-day field manual that gives you a step-by-step
            protocol for the moment the spiral starts—so you can stay present,
            lead the pace, and stop treating sex like an exam.
          </p>
          <p className="text-base md:text-lg italic text-[#f5f0e8]/70 border-l-2 border-[#c9a962] pl-4" data-id="element-236">
            No cringe. No "confidence speeches." Just a repeatable sequence.
          </p>

          <div className="pt-4 md:pt-6 text-center" data-id="element-237">
            <Button onClick={scrollToPricing} className="w-full md:w-auto" data-id="element-238">
              <span className="flex items-center justify-center gap-2" data-id="element-239">
                <Gift className="w-4 h-4" data-id="element-240" />
                <span className="ml-1" data-id="element-241">Get Everything for Only $17</span>
              </span>
            </Button>
            <p className="mt-2 md:mt-3 text-xs md:text-sm text-[#f5f0e8]/50 flex items-center justify-center gap-1" data-id="element-242">
              <Download className="w-3 h-3" data-id="element-243" /> PDF + Audio Book + 2 Bonuses ($97
              value) included
            </p>
          </div>
        </div>
      </Section>

      {/* SECTION 2: THE PROBLEM */}
      <Section id="problem" darker className="border-y border-[#ffffff]/5 relative z-10" data-id="element-244">
        <div className="max-w-2xl mx-auto" data-id="element-245">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-6 md:mb-8 text-center" data-id="element-246">
            This is the part nobody tells you:
          </h2>

          {/* Frustrated man image */}
          <div className="mb-8 rounded-lg overflow-hidden shadow-2xl border border-[#ffffff]/10" data-id="element-247">
            <img src="https://cdn.magicpatterns.com/uploads/kqMN4j8Y38vCLBrosYJ2Gk/heylloyd-image-1770074930019.png" alt="Man experiencing frustration and anxiety" className="w-full" data-id="element-248" />
          </div>

          <div className="space-y-4 md:space-y-6 text-base md:text-lg text-[#f5f0e8]/80 leading-relaxed" data-id="element-249">
            <p data-id="element-250">Sometimes the more you like her… the worse it gets.</p>
            <p data-id="element-251">Because now it matters.</p>
            <p data-id="element-252">
              And when it matters, your brain starts running a silent background
              check:
            </p>

            <ul className="pl-4 md:pl-6 space-y-2 border-l-2 border-[#c9a962]/30 my-4 md:my-6" data-id="element-253">
              <li className="pl-2" data-id="element-254">"Is it happening yet?"</li>
              <li className="pl-2" data-id="element-255">"Is she noticing?"</li>
              <li className="pl-2" data-id="element-256">"What if I lose it again?"</li>
              <li className="pl-2" data-id="element-257">"What does this say about me?"</li>
            </ul>

            <p data-id="element-258">
              That internal scan doesn't feel like fear. It feels like "being
              realistic."
            </p>
            <p data-id="element-259">But your body reads it as danger.</p>
            <p data-id="element-260">
              And when your nervous system flips into threat mode, arousal
              becomes the first thing that disappears.
            </p>
            <p className="text-lg md:text-xl font-medium text-[#f5f0e8] pt-2 md:pt-4" data-id="element-261">
              Not because you're broken.
              <br data-id="element-262" />
              <span className="text-[#c9a962]" data-id="element-263">
                Because you're monitoring yourself.
              </span>
            </p>
          </div>
        </div>
      </Section>

      {/* SECTION 3: IDENTIFICATION - Single column layout */}
      <Section className="relative z-10" data-id="element-264">
        <div className="max-w-2xl mx-auto" data-id="element-265">
          {/* Image first */}
          <div className="mb-8" data-id="element-266">
            <img src="https://cdn.magicpatterns.com/uploads/em594LMRL5VrGh7DZJLHCS/heylloyd-image-1770073164544.png" alt="Man contemplating" className="rounded-lg shadow-2xl border border-[#ffffff]/10 w-full" data-id="element-267" />
          </div>

          {/* Content */}
          <div data-id="element-268">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-6 md:mb-8 text-center" data-id="element-269">
              If any of this is true, you're the target
            </h2>

            <div className="space-y-1" data-id="element-270">
              <CheckItem data-id="element-271">
                You can get erections alone (or in the morning)… but with a
                partner you spiral.
              </CheckItem>
              <CheckItem data-id="element-272">
                One bad night created a fear that now shows up every time.
              </CheckItem>
              <CheckItem data-id="element-273">
                You rush because you're afraid your body will "change its mind."
              </CheckItem>
              <CheckItem data-id="element-274">You read her face like it's a verdict.</CheckItem>
              <CheckItem data-id="element-275">
                You've tried pills, tips, supplements, "relax," porn breaks… and
                you still can't control the moment.
              </CheckItem>
            </div>

            <p className="mt-6 md:mt-8 text-base md:text-lg font-medium text-[#c9a962] border-t border-[#ffffff]/10 pt-4 md:pt-6 text-center" data-id="element-276">
              This guide is for the man who doesn't want to talk about it for
              six months. He wants something he can use tonight.
            </p>
          </div>
        </div>
      </Section>

      {/* SECTION 4: WHY OLD SOLUTIONS FAIL */}
      <Section darker className="text-center border-y border-[#ffffff]/5 relative z-10" data-id="element-277">
        <div className="max-w-2xl mx-auto" data-id="element-278">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-6 md:mb-8" data-id="element-279">
            Why "just relax" fails
          </h2>

          <p className="text-lg md:text-xl text-[#f5f0e8]/90 mb-4 md:mb-6" data-id="element-280">
            Because "relax" isn't an instruction.
          </p>

          <p className="text-base md:text-lg text-[#f5f0e8]/70 mb-6 md:mb-8" data-id="element-281">
            It's like telling someone in a skid to "drive better."
          </p>

          <div className="bg-[#1a1f2e] p-6 md:p-8 rounded-xl border border-[#c9a962]/20 shadow-lg" data-id="element-282">
            <p className="text-lg md:text-xl font-medium leading-relaxed" data-id="element-283">
              When the spiral hits, you need a{' '}
              <span className="text-[#c9a962]" data-id="element-284">sequence</span>—a set of moves
              that change your state and redirect your attention, fast.
            </p>
            <p className="mt-3 md:mt-4 text-[#f5f0e8]/60 uppercase tracking-widest text-xs md:text-sm font-bold" data-id="element-285">
              That's what this is.
            </p>
          </div>
        </div>
      </Section>

      {/* SECTION 5: THE MECHANISM */}
      <Section id="solution" className="relative overflow-hidden z-10" data-id="element-286">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-4xl bg-[#c9a962]/5 blur-[100px] -z-10" data-id="element-287"></div>

        <div className="text-center mb-8 md:mb-16" data-id="element-288">
          <h2 className="text-xs md:text-sm font-bold tracking-widest text-[#c9a962] uppercase mb-2 md:mb-3" data-id="element-289">
            Introducing
          </h2>
          <h3 className="text-2xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4" data-id="element-290">
            The Spectatoring Reset (50+)
          </h3>
          <p className="text-base md:text-lg lg:text-xl text-[#f5f0e8]/70" data-id="element-291">
            A 14-day, step-by-step protocol built around one mechanism:
          </p>
        </div>

        <div className="max-w-2xl mx-auto mb-8 md:mb-16" data-id="element-292">
          <div className="bg-[#c9a962]/10 border border-[#c9a962]/30 rounded-2xl p-5 md:p-12 text-center" data-id="element-293">
            <h4 className="text-xl md:text-2xl lg:text-3xl font-bold text-[#c9a962] mb-4 md:mb-6 font-serif" data-id="element-294">
              The Spectatoring Kill-Switch Protocol
            </h4>
            <img src="https://cdn.magicpatterns.com/uploads/rWJxdGbwYTn7PHLMNr7P8d/heylloyd-image-1770060089670.png" alt="Downshift → Re-Anchor → No-Fail Ladder" className="w-full max-w-xl mx-auto" data-id="element-295" />
          </div>
          <p className="text-center mt-6 md:mt-8 text-base md:text-lg text-[#f5f0e8]/70 max-w-2xl mx-auto" data-id="element-296">
            It's not motivation. It's not affirmations. It's not a "be more
            confident" lecture. It's a field manual for the moment you start
            checking yourself.
          </p>
        </div>

        {/* Cards - stack on mobile, row on larger screens */}
        <div className="space-y-4 md:space-y-0 md:grid md:grid-cols-3 md:gap-6 max-w-4xl mx-auto" data-id="element-297">
          <Card title="1) Downshift" icon={Clock} data-id="element-298">
            <span className="block text-xs md:text-sm text-[#c9a962] mb-2 font-mono" data-id="element-299">
              60–90 SECONDS
            </span>
            A discreet nervous-system reset you can do while kissing, touching,
            or stepping away for a moment.
            <br data-id="element-300" />
            <br data-id="element-301" />
            <strong className="text-[#f5f0e8]" data-id="element-302">Purpose:</strong> stop your body
            from treating intimacy like a threat.
          </Card>

          <Card title="2) Re-Anchor" icon={Shield} data-id="element-303">
            <span className="block text-xs md:text-sm text-[#c9a962] mb-2 font-mono" data-id="element-304">
              ATTENTION CONTROL
            </span>
            A simple attention strategy that pulls you out of scoreboard
            thinking and back into sensation and connection.
            <br data-id="element-305" />
            <br data-id="element-306" />
            <strong className="text-[#f5f0e8]" data-id="element-307">Purpose:</strong> break
            spectatoring in real time.
          </Card>

          <Card title="3) No-Fail Ladder" icon={BookOpen} data-id="element-308">
            <span className="block text-xs md:text-sm text-[#c9a962] mb-2 font-mono" data-id="element-309">
              PROGRESSION
            </span>
            A progression that removes the single pass/fail moment.
            <br data-id="element-310" />
            <br data-id="element-311" />
            <strong className="text-[#f5f0e8]" data-id="element-312">Purpose:</strong> even if you
            wobble, you don't panic—because you already know what to do next.
          </Card>
        </div>
      </Section>

      {/* SECTION 6: WHY OLD SOLUTIONS MAKE IT WORSE */}
      <Section darker className="border-y border-[#ffffff]/5 relative z-10" data-id="element-313">
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-8 md:mb-12 text-center" data-id="element-314">
          Why old solutions keep making it worse
        </h2>

        <div className="space-y-4 md:space-y-6 max-w-2xl mx-auto" data-id="element-315">
          <div className="flex gap-3 md:gap-4" data-id="element-316">
            <div className="w-1 bg-red-500/20 rounded-full flex-shrink-0" data-id="element-317"></div>
            <div data-id="element-318">
              <h4 className="text-lg md:text-xl font-bold text-[#f5f0e8] mb-1 md:mb-2" data-id="element-319">
                Pills alone
              </h4>
              <p className="text-sm md:text-base text-[#f5f0e8]/70" data-id="element-320">
                Can work physically but increase psychological pressure: "Now I{' '}
                <em data-id="element-321">have</em> to perform."
              </p>
            </div>
          </div>

          <div className="flex gap-3 md:gap-4" data-id="element-322">
            <div className="w-1 bg-red-500/20 rounded-full flex-shrink-0" data-id="element-323"></div>
            <div data-id="element-324">
              <h4 className="text-lg md:text-xl font-bold text-[#f5f0e8] mb-1 md:mb-2" data-id="element-325">
                Random tips
              </h4>
              <p className="text-sm md:text-base text-[#f5f0e8]/70" data-id="element-326">
                Don't solve the moment of panic.
              </p>
            </div>
          </div>

          <div className="flex gap-3 md:gap-4" data-id="element-327">
            <div className="w-1 bg-red-500/20 rounded-full flex-shrink-0" data-id="element-328"></div>
            <div data-id="element-329">
              <h4 className="text-lg md:text-xl font-bold text-[#f5f0e8] mb-1 md:mb-2" data-id="element-330">
                Trying harder
              </h4>
              <p className="text-sm md:text-base text-[#f5f0e8]/70" data-id="element-331">
                Makes you monitor harder. And monitoring is the match that
                lights the spiral.
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* SECTION 7: WHAT YOU GET */}
      <Section id="benefits" className="relative z-10" data-id="element-332">
        <div className="bg-[#151a23] border border-[#ffffff]/5 rounded-2xl p-5 md:p-12 shadow-2xl max-w-2xl mx-auto" data-id="element-333">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-6 md:mb-8 text-center" data-id="element-334">
            What you get inside
          </h2>

          <div className="space-y-2 md:space-y-4" data-id="element-335">
            <CheckItem data-id="element-336">
              The exact loop that causes "works alone / fails with her"
            </CheckItem>
            <CheckItem data-id="element-337">
              The 90-second Downshift sequence (do it discreetly)
            </CheckItem>
            <CheckItem data-id="element-338">3 Re-Anchors (pick one and install it)</CheckItem>
            <CheckItem data-id="element-339">
              The No-Fail Ladder so you stop "testing yourself"
            </CheckItem>
            <CheckItem data-id="element-340">
              A short pre-game script that removes pressure without dumping a
              confession
            </CheckItem>
            <CheckItem data-id="element-341">
              A 14-day plan with trackers and a night-of checklist
            </CheckItem>
            <CheckItem data-id="element-342">
              Relapse-proofing: what to do after a bad night so it doesn't
              become your identity
            </CheckItem>
          </div>
        </div>
      </Section>

      {/* SECTION 8: WHO THIS IS FOR */}
      <Section darker className="text-center relative z-10" data-id="element-343">
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-8 md:mb-12" data-id="element-344">
          Who this is for (and not for)
        </h2>

        <div className="space-y-4 md:space-y-0 md:grid md:grid-cols-2 md:gap-8 max-w-2xl mx-auto" data-id="element-345">
          <div className="bg-[#1a1f2e] p-5 md:p-8 rounded-xl border-t-4 border-[#c9a962]" data-id="element-346">
            <h3 className="text-lg md:text-xl font-bold mb-3 md:mb-4 text-[#c9a962]" data-id="element-347">
              For
            </h3>
            <p className="text-sm md:text-base text-[#f5f0e8]/80" data-id="element-348">
              Men 50+ who are dating again and feel that "judged / replaced /
              humiliated" pressure.
            </p>
          </div>

          <div className="bg-[#1a1f2e] p-5 md:p-8 rounded-xl border-t-4 border-gray-600" data-id="element-349">
            <h3 className="text-lg md:text-xl font-bold mb-3 md:mb-4 text-gray-400" data-id="element-350">
              Not For
            </h3>
            <p className="text-sm md:text-base text-[#f5f0e8]/80" data-id="element-351">
              Anyone looking for medical diagnosis, or expecting a guaranteed
              outcome from a PDF.
            </p>
          </div>
        </div>
      </Section>

      {/* TESTIMONIALS SECTION */}
      <section id="testimonials" className="relative z-10" data-id="element-352">
        <TestimonialsSection data-id="element-353" />
      </section>

      {/* SECTION: FREE BONUSES */}
      <Section className="relative z-10" data-id="element-354">
        <div className="text-center mb-8 md:mb-12" data-id="element-355">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="inline-flex items-center gap-2 px-4 py-2 bg-[#c9a962]/20 border border-[#c9a962]/30 rounded-full mb-4" data-id="element-356">
            <Gift className="w-5 h-5 text-[#c9a962]" data-id="element-357" />
            <span className="text-[#c9a962] font-bold" data-id="element-358">LIMITED TIME OFFER</span>
          </motion.div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4" data-id="element-359">
            Order Today & Get{' '}
            <span className="text-[#c9a962]" data-id="element-360">2 FREE Bonuses</span>
          </h2>
          <p className="text-[#f5f0e8]/70 text-lg max-w-xl mx-auto" data-id="element-361">
            Worth <span className="text-[#c9a962] font-bold" data-id="element-362">$97</span> — Yours
            FREE when you get The Spectatoring Reset today
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto" data-id="element-363">
          <BonusCard number={1} image={BONUS_1_IMAGE} title="The Text Zero-Pressure Kit (50+)" subtitle="21 ready-to-use messages to lower pressure and lead the rhythm before you even meet" value="$47" data-id="element-364" />
          <BonusCard number={2} image={BONUS_2_IMAGE} title="Pressure-to-Play Date Blueprint (50+)" subtitle="3-phase rhythm route for intimacy without rush — from first touch to full connection" value="$50" data-id="element-365" />
        </div>

        <div className="text-center mt-8" data-id="element-366">
          <p className="text-[#f5f0e8]/60 text-sm" data-id="element-367">
            These bonuses are only available for a limited time. Don't miss out.
          </p>
        </div>
      </Section>

      {/* SECTION 9: FINAL CTA */}
      <Section id="pricing" className="text-center pb-12 md:pb-24 relative z-10" darker data-id="element-368">
        <div className="max-w-2xl mx-auto" data-id="element-369">
          <h2 className="text-lg md:text-xl lg:text-2xl font-bold mb-4 md:mb-6 text-[#f5f0e8]/60 uppercase tracking-widest" data-id="element-370">
            The point is simple:
          </h2>
          <p className="text-2xl md:text-3xl lg:text-4xl font-serif font-bold mb-8 md:mb-12 leading-tight" data-id="element-371">
            You don't need to be fearless. <br data-id="element-372" />
            <span className="text-[#c9a962]" data-id="element-373">
              You need to be skilled at the moment fear shows up.
            </span>
          </p>

          <div className="bg-[#1a1f2e] p-6 md:p-8 rounded-2xl border border-[#c9a962]/30 shadow-2xl w-full max-w-md mx-auto" data-id="element-374">
            {/* Total Value Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#c9a962]/20 border border-[#c9a962]/30 rounded-full mb-2" data-id="element-375">
              <Gift className="w-4 h-4 text-[#c9a962]" data-id="element-376" />
              <span className="text-[#c9a962] font-bold text-sm" data-id="element-377">
                Total Value: $131+
              </span>
            </div>

            {/* 50% OFF Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-500/20 border border-green-500/30 rounded-full mb-4 ml-2" data-id="element-378">
              <Zap className="w-4 h-4 text-green-400" data-id="element-379" />
              <span className="text-green-400 font-bold text-sm" data-id="element-380">
                87% OFF — Limited Time
              </span>
            </div>

            <div className="mb-4 md:mb-6" data-id="element-381">
              <div className="flex items-center justify-center gap-2" data-id="element-382">
                <span className="text-2xl text-[#f5f0e8]/40 line-through" data-id="element-383">
                  $131
                </span>
                <span className="text-4xl md:text-5xl font-bold text-[#f5f0e8]" data-id="element-384">
                  $17
                </span>
              </div>
              <span className="text-[#f5f0e8]/50 text-base md:text-lg" data-id="element-385">
                one-time payment
              </span>
            </div>

            {/* What's Included */}
            <div className="bg-[#0a0d12] rounded-xl p-4 mb-6 text-left" data-id="element-386">
              <p className="text-xs uppercase tracking-wider text-[#c9a962] font-bold mb-3" data-id="element-387">
                Everything You Get:
              </p>
              <div className="space-y-3" data-id="element-388">
                <div className="flex items-center gap-3" data-id="element-389">
                  <div className="w-10 h-10 rounded-lg bg-[#c9a962]/20 flex items-center justify-center flex-shrink-0" data-id="element-390">
                    <FileText className="w-5 h-5 text-[#c9a962]" data-id="element-391" />
                  </div>
                  <div className="flex-1" data-id="element-392">
                    <p className="text-[#f5f0e8] font-semibold text-sm" data-id="element-393">
                      PDF Ebook
                    </p>
                    <p className="text-[#f5f0e8]/50 text-xs" data-id="element-394">
                      The complete 14-day protocol
                    </p>
                  </div>
                  <span className="text-[#f5f0e8]/40 text-xs" data-id="element-395">$34</span>
                </div>
                <div className="flex items-center gap-3" data-id="element-396">
                  <div className="w-10 h-10 rounded-lg bg-[#c9a962]/20 flex items-center justify-center flex-shrink-0" data-id="element-397">
                    <Headphones className="w-5 h-5 text-[#c9a962]" data-id="element-398" />
                  </div>
                  <div className="flex-1" data-id="element-399">
                    <p className="text-[#f5f0e8] font-semibold text-sm" data-id="element-400">
                      Full Audio Book
                    </p>
                    <p className="text-[#f5f0e8]/50 text-xs" data-id="element-401">
                      Listen anywhere, anytime
                    </p>
                  </div>
                  <span className="text-[#f5f0e8]/40 text-xs" data-id="element-402">Included</span>
                </div>

                {/* Divider */}
                <div className="border-t border-[#ffffff]/10 my-2 pt-2" data-id="element-403">
                  <p className="text-xs uppercase tracking-wider text-green-400 font-bold mb-2 flex items-center gap-1" data-id="element-404">
                    <Gift className="w-3 h-3" data-id="element-405" /> Free Bonuses
                  </p>
                </div>

                <div className="flex items-center gap-3" data-id="element-406">
                  <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0" data-id="element-407">
                    <MessageSquare className="w-5 h-5 text-green-400" data-id="element-408" />
                  </div>
                  <div className="flex-1" data-id="element-409">
                    <p className="text-[#f5f0e8] font-semibold text-sm" data-id="element-410">
                      Text Zero-Pressure Kit
                    </p>
                    <p className="text-[#f5f0e8]/50 text-xs" data-id="element-411">
                      21 messages to lower pressure
                    </p>
                  </div>
                  <span className="text-green-400 text-xs font-bold" data-id="element-412">FREE</span>
                </div>
                <div className="flex items-center gap-3" data-id="element-413">
                  <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0" data-id="element-414">
                    <Heart className="w-5 h-5 text-green-400" data-id="element-415" />
                  </div>
                  <div className="flex-1" data-id="element-416">
                    <p className="text-[#f5f0e8] font-semibold text-sm" data-id="element-417">
                      Pressure-to-Play Blueprint
                    </p>
                    <p className="text-[#f5f0e8]/50 text-xs" data-id="element-418">
                      3-phase intimacy roadmap
                    </p>
                  </div>
                  <span className="text-green-400 text-xs font-bold" data-id="element-419">FREE</span>
                </div>

                <div className="flex items-center gap-3" data-id="element-420">
                  <div className="w-10 h-10 rounded-lg bg-[#c9a962]/20 flex items-center justify-center flex-shrink-0" data-id="element-421">
                    <Download className="w-5 h-5 text-[#c9a962]" data-id="element-422" />
                  </div>
                  <div className="flex-1" data-id="element-423">
                    <p className="text-[#f5f0e8] font-semibold text-sm" data-id="element-424">
                      Instant Download
                    </p>
                    <p className="text-[#f5f0e8]/50 text-xs" data-id="element-425">
                      Access everything immediately
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <Button onClick={goToCheckout} className="w-full text-base md:text-lg py-4 md:py-5 mb-3 md:mb-4" data-id="element-426">
              Get Everything for Only $17
            </Button>

            <p className="text-xs md:text-sm text-[#f5f0e8]/50 flex items-center justify-center" data-id="element-427">
              <Lock className="w-3 h-3 mr-1" data-id="element-428" /> Secure checkout. Private. No
              subscription.
            </p>

            {/* 7-Day Guarantee */}
            <GuaranteeBadge data-id="element-429" />
          </div>
        </div>
      </Section>

      {/* FOOTER */}
      <footer className="py-6 md:py-8 px-4 text-center border-t border-[#ffffff]/5 relative z-10" data-id="element-430">
        <div className="max-w-3xl mx-auto" data-id="element-431">
          <div className="flex flex-col md:flex-row items-center justify-center gap-2 md:gap-6 mb-4" data-id="element-432">
            <button onClick={() => setPrivacyOpen(true)} className="text-[#f5f0e8]/50 hover:text-[#c9a962] text-sm transition-colors" data-id="element-433">
              Privacy Policy
            </button>
            <span className="hidden md:inline text-[#f5f0e8]/20" data-id="element-434">|</span>
            <button onClick={() => setTermsOpen(true)} className="text-[#f5f0e8]/50 hover:text-[#c9a962] text-sm transition-colors" data-id="element-435">
              Terms of Service
            </button>
            <span className="hidden md:inline text-[#f5f0e8]/20" data-id="element-436">|</span>
            <button onClick={goToCheckout} className="text-[#f5f0e8]/50 hover:text-[#c9a962] text-sm transition-colors" data-id="element-437">
              Get The Guide
            </button>
          </div>
          <p className="text-[#f5f0e8]/30 text-xs md:text-sm" data-id="element-438">
            © {new Date().getFullYear()} The Spectatoring Reset. All rights
            reserved.
          </p>
          <p className="text-[#f5f0e8]/20 text-xs mt-2" data-id="element-439">
            This product is for educational purposes only and is not intended to
            diagnose, treat, or cure any medical condition.
          </p>
        </div>
      </footer>

      {/* Privacy Modal */}
      <PrivacyModal isOpen={privacyOpen} onClose={() => setPrivacyOpen(false)} data-id="element-440" />

      {/* Terms Modal */}
      <TermsModal isOpen={termsOpen} onClose={() => setTermsOpen(false)} data-id="element-441" />
    </div>;
}
// Checkout URL
const CHECKOUT_URL = 'https://vitaliarutinas.com/cart/44438743449679:1';